import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useLicitacoesFeed } from '@/hooks/useLicitacoes';
import type { EmpresaListItem } from '@/types/empresa';
import {
  useCriarEventoCalendario, useAtualizarEventoCalendario, useExcluirEventoCalendario,
  type CalendarioEvento, type CalendarioTipoEvento,
} from '@/hooks/useCalendario';

interface VincularEventoModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresas: EmpresaListItem[];
  defaultEmpresaId?: number;
  defaultDate: string;
  eventoParaEditar?: CalendarioEvento | null;
}

const TIPOS_EVENTO: { value: CalendarioTipoEvento; label: string }[] = [
  { value: 'prazo', label: 'Prazo' },
  { value: 'visita_tecnica', label: 'Visita técnica' },
  { value: 'abertura_proposta', label: 'Abertura de proposta' },
  { value: 'outro', label: 'Outro' },
];

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

function VincularEventoModal({ isOpen, onClose, empresas, defaultEmpresaId, defaultDate, eventoParaEditar }: VincularEventoModalProps) {
  const isEditMode = !!eventoParaEditar;

  const [empresaId, setEmpresaId] = useState('');
  const [licitacaoSearch, setLicitacaoSearch] = useState('');
  const [licitacaoId, setLicitacaoId] = useState('');
  const [dataEvento, setDataEvento] = useState(defaultDate);
  const [tipoEvento, setTipoEvento] = useState<CalendarioTipoEvento>('prazo');
  const [observacoes, setObservacoes] = useState('');

  const { data: licitacoesFeed, isLoading: licitacoesLoading } = useLicitacoesFeed({
    search: licitacaoSearch || undefined,
    limit: 20,
  });

  const empresaIdAtiva = isEditMode ? eventoParaEditar.empresaId : (parseInt(empresaId, 10) || undefined);

  const criarMutation = useCriarEventoCalendario(empresaIdAtiva);
  const atualizarMutation = useAtualizarEventoCalendario(empresaIdAtiva);
  const excluirMutation = useExcluirEventoCalendario(empresaIdAtiva);

  useEffect(() => {
    if (!isOpen) return;

    if (eventoParaEditar) {
      setDataEvento(eventoParaEditar.dataEvento.slice(0, 10));
      setTipoEvento(eventoParaEditar.tipoEvento);
      setObservacoes(eventoParaEditar.observacoes || '');
    } else {
      setEmpresaId(defaultEmpresaId ? String(defaultEmpresaId) : '');
      setLicitacaoSearch('');
      setLicitacaoId('');
      setDataEvento(defaultDate);
      setTipoEvento('prazo');
      setObservacoes('');
    }
  }, [isOpen, eventoParaEditar, defaultDate, defaultEmpresaId]);

  const licitacoes = licitacoesFeed?.licitacoes || [];
  const mutationError = (criarMutation.error || atualizarMutation.error) as { message?: string } | null;

  const salvar = async () => {
    if (isEditMode) {
      await atualizarMutation.mutateAsync({
        eventoId: eventoParaEditar.id,
        dataEvento,
        tipoEvento,
        observacoes: observacoes || undefined,
      });
    } else {
      const licitacaoIdNum = parseInt(licitacaoId, 10);
      if (!empresaIdAtiva || !licitacaoIdNum || !dataEvento) return;
      await criarMutation.mutateAsync({
        licitacaoId: licitacaoIdNum,
        dataEvento,
        tipoEvento,
        observacoes: observacoes || undefined,
      });
    }
    onClose();
  };

  const excluir = async () => {
    if (!eventoParaEditar) return;
    await excluirMutation.mutateAsync(eventoParaEditar.id);
    onClose();
  };

  const podeSalvar = isEditMode ? true : !!empresaIdAtiva && !!licitacaoId && !!dataEvento;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? 'Editar vínculo' : 'Vincular licitação a uma data'}>
      <div className="space-y-4">
        {isEditMode ? (
          <div className="rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5">
            <p className="text-xs font-semibold text-ds-info">{eventoParaEditar.empresaNome}</p>
            <p className="text-sm font-medium text-ds-text-primary">{eventoParaEditar.licitacao.objeto}</p>
            <p className="text-xs text-ds-text-secondary">{eventoParaEditar.licitacao.orgao || 'Órgão não informado'}</p>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="calendario-empresa">
                Empresa (cliente)
              </label>
              <select
                id="calendario-empresa"
                className={selectClassName}
                value={empresaId}
                onChange={(e) => setEmpresaId(e.target.value)}
              >
                <option value="">Selecione uma empresa</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>{empresa.razao_social}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="calendario-licitacao-search">
                Licitação
              </label>
              <Input
                id="calendario-licitacao-search"
                placeholder="Buscar por objeto, órgão..."
                value={licitacaoSearch}
                onChange={(e) => setLicitacaoSearch(e.target.value)}
                className="mb-2"
              />
              <select
                aria-label="Resultado da busca de licitação"
                className={selectClassName}
                value={licitacaoId}
                onChange={(e) => setLicitacaoId(e.target.value)}
                disabled={licitacoesLoading}
              >
                <option value="">Selecione uma licitação</option>
                {licitacoes.map((licitacao) => (
                  <option key={licitacao.id} value={licitacao.id}>
                    {licitacao.numero_edital ? `${licitacao.numero_edital} — ` : ''}
                    {licitacao.objeto}
                    {licitacao.orgao ? ` (${licitacao.orgao})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="calendario-data-evento">
              Data do evento
            </label>
            <Input
              id="calendario-data-evento"
              type="date"
              value={dataEvento}
              onChange={(e) => setDataEvento(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="calendario-tipo-evento">
              Tipo de evento
            </label>
            <select
              id="calendario-tipo-evento"
              className={selectClassName}
              value={tipoEvento}
              onChange={(e) => setTipoEvento(e.target.value as CalendarioTipoEvento)}
            >
              {TIPOS_EVENTO.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="calendario-observacoes">
            Observações
          </label>
          <textarea
            id="calendario-observacoes"
            className={selectClassName}
            rows={3}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Opcional"
          />
        </div>

        {mutationError && (
          <p className="text-sm text-ds-danger">Falha ao salvar. Tente novamente.</p>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          {isEditMode ? (
            <Button type="button" variant="danger" size="sm" isLoading={excluirMutation.isPending} onClick={excluir}>
              Remover vínculo
            </Button>
          ) : <span />}

          <Button
            type="button"
            disabled={!podeSalvar}
            isLoading={criarMutation.isPending || atualizarMutation.isPending}
            onClick={salvar}
          >
            Salvar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { VincularEventoModal };
